import React from 'react';
import { ArrowRight, Zap, Globe, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NetworkBackground } from './NetworkBackground';

export function Hero2() {
  const { t } = useTranslation();

  return (
    <div className="relative bg-[#080e1a] overflow-hidden min-h-screen flex items-center">

      {/* Network animation */}
      <NetworkBackground />

      {/* Radial glow top-center */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-[#8B5CF6] opacity-10 blur-[120px] rounded-full pointer-events-none" />

      {/* Radial glow bottom-right accent */}
      <div className="absolute bottom-0 right-0 w-[500px] h-[400px] bg-[#D946A8] opacity-5 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
        <div className="text-center">

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#8B5CF6]/30 bg-[#8B5CF6]/10 text-[#8B5CF6] text-sm font-medium mb-8">
            <Zap className="h-3.5 w-3.5" />
            AI-Powered PMS Connectivity
          </div>

          {/* Headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.08] mb-6">
            <span className="text-white">{t('hero.title')}</span>
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(90deg, #8B5CF6 0%, #D946A8 50%, #EF4444 100%)' }}
            >
              {t('hero.subtitle')}
            </span>
          </h1>

          {/* Description — highlight on its own line (EN / FR) */}
          <p className="mt-4 text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            <span className="block">{t('hero.description')}</span>
            <span className="block mt-2 text-white font-semibold">
              {t('hero.descriptionHighlight')}
            </span>
          </p>

          {/* CTA */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/onboarding/saas"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl text-base font-semibold text-white transition-all duration-200 shadow-lg shadow-[#8B5CF6]/25 hover:shadow-[#8B5CF6]/40 hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #D946A8 100%)' }}
            >
              {t('hero.cta')}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/api-documentation"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-base font-semibold text-gray-300 border border-gray-700 hover:border-gray-500 hover:text-white transition-all duration-200"
            >
              {t('hero.docLink', 'View API docs')}
            </Link>
          </div>

          {/* Social proof pills */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-[#3ECF8E]" />
              <span>{t('hero.pills.mcp')}</span>
            </div>
            <div className="h-4 w-px bg-gray-700" />
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-[#8B5CF6]" />
              <span>{t('hero.pills.pms')}</span>
            </div>
            <div className="h-4 w-px bg-gray-700" />
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-400" />
              <span>{t('hero.pills.sync')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
